import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CommonService, formatDatetoYYYYMMDD } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { KnowledgeService } from 'src/app/services/knowledge/knowledge.service'
import { Actions, ofType } from '@ngrx/effects'
import { UPDATE_KNOWLEDGE_ARTICLE_SUCCESS_ACTION } from 'src/app/store/action/knowledge.action'
import { CategorySelection, KnowledgeAPIModel, SelectedCompanyAPIModel } from 'src/app/models/api.model'
import * as moment from 'moment'
import { Subscription } from 'rxjs'
import { ViewKnowledgeArticleComponent } from '../view-knowledge-article/view-knowledge-article.component'
import Quill from 'quill'

@Component({
  selector: 'app-create-knowledge-article',
  templateUrl: './edit-knowledge-article.component.html',
  styleUrls: ['./edit-knowledge-article.component.scss']
})
export class EditKnowledgeArticleComponent implements OnInit, OnDestroy {
  @ViewChild('container', { read: ViewContainerRef, static: true })
  container: ViewContainerRef

  private readonly subscriptions: Subscription = new Subscription()
  readonly DISPLAY_FROM_DATETIME_CONTROL_NAME = 'displayFromDatetime'
  readonly DISPLAY_TO_DATETIME_CONTROL_NAME = 'displayToDatetime'
  quillEditor: any
  isPreviewVisible = false
  knowledgeArticle: KnowledgeAPIModel
  createKnowledgeArticleFormGroup: FormGroup
  editKnowledgeArticleFormGroup: FormGroup
  filesToBeUploaded: FileList
  acceptedFormat = `Accepted File Formats: PDF; Max Size: 2 MB`
  isAddScreen: boolean = true
  showCompanySelectorError = false
  currentFormGroup = null
  private componentRef: ComponentRef<ViewKnowledgeArticleComponent>
  pageHeading: string = 'Add Knowledge Article'

  public editorConfig = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      [
        { header: '1' },
        { header: '2' },
        { header: '3' },
        { header: '4' },
        { header: '5' },
        { header: '6' },
        { font: ['times-new-roman', 'roboto', 'calibri', 'georgia', 'verdana', 'arial'] }
      ],
      [{ color: [] }, { background: [] }],
      ['image'],
      [
        {
          size: [
            '4px',
            '6px',
            '8px',
            '10px',
            '12px',
            '14px',
            '16px',
            '18px',
            '20px',
            '22px',
            '24px',
            '26px',
            '28px',
            '30px',
            '32px',
            '34px',
            '36px',
            '38px',
            '40px',
            '42px',
            '44px',
            '46px',
            '48px',
            '50px',
            '52px',
            '54px',
            '56px',
            '58px',
            '60px'
          ]
        }
      ]
    ]
  }
  canPublish: boolean
  selectedCompany: SelectedCompanyAPIModel

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly knowledgeService: KnowledgeService,
    private readonly cs: CommonService,
    private readonly route: ActivatedRoute,
    private readonly documentService: DocumentService,
    private readonly actions$: Actions,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly router: Router
  ) {}

  openModal() {
    this.isPreviewVisible = true
    const factory = this.componentFactoryResolver.resolveComponentFactory(ViewKnowledgeArticleComponent)
    this.container.clear()
    this.componentRef = this.container.createComponent(factory)
    this.componentRef.instance.openedInModel = true
  }

  closeModal() {
    this.isPreviewVisible = false
    if (this.componentRef) {
      this.componentRef.destroy()
    }
  }

  ngOnInit() {
    const Font = Quill.import('formats/font') as any
    Font.whitelist = ['roboto', 'times-new-roman', 'calibri', 'georgia', 'verdana', 'arial']
    Quill.register(Font, true)

    const SizeStyle = Quill.import('attributors/style/size') as any
    SizeStyle.whitelist = [
      '4px',
      '6px',
      '8px',
      '10px',
      '12px',
      '14px',
      '16px',
      '18px',
      '20px',
      '22px',
      '24px',
      '26px',
      '28px',
      '30px',
      '32px',
      '34px',
      '36px',
      '38px',
      '40px',
      '42px',
      '44px',
      '46px',
      '48px',
      '50px',
      '52px',
      '54px',
      '56px',
      '58px',
      '60px'
    ]

    Quill.register(SizeStyle, true)

    this.buildCreateKnowledgeArticleFormGroup()
    this.buildEditKnowledgeArticleFormGroup()
    this.subscribeToRouteParams()
    this.getKnowledgeArticleFromStore()
    this.subscribeToUpdateSuccessAction()
    if (this.isAddScreen) {
      this.currentFormGroup = this.createKnowledgeArticleFormGroup
    } else {
      this.currentFormGroup = this.editKnowledgeArticleFormGroup
    }
    if (this.router.url.endsWith('edit')) {
      this.pageHeading = 'Edit Knowledge Bank'
    }
  }

  private subscribeToRouteParams() {
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        const id = params['id']
        if (id != 1) {
          this.knowledgeService.dispatchViewKnowledgeAction(id, false)
          this.isAddScreen = false
          this.currentFormGroup = this.editKnowledgeArticleFormGroup
        }
      })
    )
  }

  private subscribeToUpdateSuccessAction() {
    this.subscriptions.add(
      this.actions$.pipe(ofType(UPDATE_KNOWLEDGE_ARTICLE_SUCCESS_ACTION)).subscribe(() => {
        if (!this.canPublish) {
          this.openModal()
        } else {
          this.knowledgeService.dispatchPublishKnowledgeAction(this.knowledgeArticle.id)
          this.canPublish = false
        }
      })
    )
  }

  private getKnowledgeArticleFromStore() {
    this.subscriptions.add(
      this.knowledgeService.getKnowledgeArticleFromStore().subscribe((knowledge: KnowledgeAPIModel) => {
        if (knowledge?.id) {
          this.populateFormWithKnowledgeData(knowledge)
          this.knowledgeArticle = knowledge
          this.updateEditorContent(knowledge.contents)
        }
      })
    )
  }

  private populateFormWithKnowledgeData(knowledge: KnowledgeAPIModel) {
    this.editKnowledgeArticleFormGroup.patchValue({
      heading: knowledge.heading,
      contents: knowledge.contents,
      referenceVideo: knowledge.referenceVideo,
      [this.DISPLAY_FROM_DATETIME_CONTROL_NAME]: formatDatetoYYYYMMDD(knowledge.displayFromDatetime),
      [this.DISPLAY_TO_DATETIME_CONTROL_NAME]: formatDatetoYYYYMMDD(knowledge.displayToDatetime),
      companyReferenceId: knowledge.companyReferenceId,
      category: knowledge.category,
      subCategory: knowledge.subCategory
    })
  }

  private updateEditorContent(contents: string) {
    if (this.quillEditor) {
      this.quillEditor.root.innerHTML = contents
    }
  }

  buildCreateKnowledgeArticleFormGroup() {
    this.createKnowledgeArticleFormGroup = this.formBuilder.group({
      heading: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(150)]],
      companyReferenceId: [null, [Validators.required]],
      category: ['', [Validators.required]],
      subCategory: ['', [Validators.required]]
    })
  }

  buildEditKnowledgeArticleFormGroup() {
    this.editKnowledgeArticleFormGroup = this.formBuilder.group({
      heading: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(150)]],
      contents: ['', [Validators.required, Validators.minLength(1)]],
      referenceVideo: [null, [Validators.pattern(this.urlPattern())]],
      [this.DISPLAY_FROM_DATETIME_CONTROL_NAME]: [''],
      [this.DISPLAY_TO_DATETIME_CONTROL_NAME]: [''],
      companyReferenceId: [{ value: null, disabled: true }]
    })
  }

  urlPattern(): RegExp {
    return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/
  }

  get f() {
    return this.currentFormGroup.controls
  }

  validateAndUpdateKnowledgeArticle(canPublish: boolean) {
    this.checkFromAndToDate()
    if (this.editKnowledgeArticleFormGroup.invalid) {
      this.editKnowledgeArticleFormGroup.markAllAsTouched()
      return
    }
    if (this.editKnowledgeArticleFormGroup.valid) {
      const knowledge = this.prepareKnowledgeData()
      const files: File[] = Array.from(this.filesToBeUploaded ?? [])
      this.knowledgeService.dispatchUpdateKnowledgeArticleAction(knowledge, this.knowledgeArticle.id, files, '')
      this.canPublish = canPublish
    }
  }

  private prepareKnowledgeData(): KnowledgeAPIModel {
    const knowledge = {
      ...this.editKnowledgeArticleFormGroup.value,
      companyReferenceId: this.knowledgeArticle.companyReferenceId
    }
    knowledge.displayFromDatetime = moment(knowledge.displayFromDatetime || new Date()).format('YYYY-MM-DD')
    knowledge.displayToDatetime = moment(knowledge.displayToDatetime || '2099-12-31').format('YYYY-MM-DD')
    knowledge.contents = this.cleanHtml(this.quillEditor.root.innerHTML)
    return knowledge
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.KNOWLEDGE_HUB, GlobalConstants.ROUTING.SEARCH)
  }

  preview() {
    this.validateAndUpdateKnowledgeArticle(false)
  }

  handleFileInput(files: FileList) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  checkFromAndToDate() {
    const fromDateControl = this.editKnowledgeArticleFormGroup.get(this.DISPLAY_FROM_DATETIME_CONTROL_NAME)
    const tillDateControl = this.editKnowledgeArticleFormGroup.get(this.DISPLAY_TO_DATETIME_CONTROL_NAME)

    const fromDate = new Date(fromDateControl.value)
    const tillDate = new Date(tillDateControl.value)
    const today = new Date()

    const fromDateWithoutTime = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
    const tillDateWithoutTime = new Date(tillDate.getFullYear(), tillDate.getMonth(), tillDate.getDate())
    const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    if (todayWithoutTime > fromDateWithoutTime) {
      this.cs.showError('Display From must not be before the current date.')
      fromDateControl.setErrors({ invalidDate: true })
      return
    } else {
      fromDateControl.setErrors(null)
    }

    if (todayWithoutTime > tillDateWithoutTime) {
      this.cs.showError('Display To must not be before the current date.')
      tillDateControl.setErrors({ invalidDate: true })
      return
    } else {
      tillDateControl.setErrors(null)
    }

    if (fromDateWithoutTime > tillDateWithoutTime) {
      this.cs.showError('The "FROM" date is after the "TO" date.')
      tillDateControl.setErrors({ invalidTillDate: true })
    } else if (fromDateWithoutTime <= tillDateWithoutTime) {
      fromDateControl.setErrors(null)
      tillDateControl.setErrors(null)
    }
  }

  onEditorCreated(editor: any): void {
    this.quillEditor = editor
    const toolbar = this.quillEditor.getModule('toolbar')
    toolbar.addHandler('image', () => {
      this.selectImage()
    })
  }

  selectImage(): void {
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/jpeg'

    fileInput.onchange = (event: any) => {
      const file = event.target.files[0]
      if (file) {
        this.uploadImage(file)
      }
    }

    fileInput.click()
  }

  uploadImage(file: File): void {
    this.documentService
      .uploadDocumentToCompany(
        GlobalConstants.MODULE.KNOWLEDGE_BANK,
        this.knowledgeArticle.id,
        'OTHER',
        file,
        this.knowledgeArticle.companyReferenceId,
        'includeTemporaryLink=true'
      )
      .subscribe(response => {
        this.insertImageInEditor(response.id, response?.temporaryLink)
      })
  }

  insertImageInEditor(documentId: string, imageUrl: string): void {
    const range = this.quillEditor.getSelection()
    if (range) {
      const position = range.index
      this.quillEditor.insertEmbed(position, 'image', imageUrl)
      const imageElement = this.quillEditor.root.querySelector(`img[src="${imageUrl}"]`)
      if (imageElement) {
        imageElement.setAttribute('data-document-id', documentId)
      }
    }
  }

  publish() {
    this.validateAndUpdateKnowledgeArticle(true)
  }

  cleanHtml(html: string): string {
    const doc = this.createDocumentFromHtml(html)
    this.removePdfAnchorsAndDivs(doc)
    return this.removeImageSrc(doc)
  }

  // Create a DOM document from HTML string
  private createDocumentFromHtml(htmlString: string): Document {
    const parser = new DOMParser()
    return parser.parseFromString(htmlString, 'text/html')
  }

  // Remove <a> tags with file-type="KA-PDF" and their parent <div> if present
  private removePdfAnchorsAndDivs(doc: Document): void {
    const anchors = doc.querySelectorAll('a[file-type="KA-PDF"]')

    anchors.forEach(anchor => {
      const parentDiv = anchor.parentElement
      if (parentDiv && parentDiv.tagName === 'DIV') {
        parentDiv.remove() // Remove the entire parent <div>
      } else {
        anchor.remove() // If no <div>, just remove the <a>
      }
    })
  }

  // Remove the src attribute from <img> tags
  private removeImageSrc(doc: Document): string {
    const htmlWithImagesRemoved = doc.body.innerHTML.replace(/<img\b[^>]*?\bsrc\s*=\s*(['"]).*?\1[^>]*>/gi, match => {
      return match.replace(/\bsrc\s*=\s*(['"]).*?\1\s*/gi, '')
    })
    return htmlWithImagesRemoved
  }

  handleCompanySelectedValue(company: SelectedCompanyAPIModel): void {
    this.selectedCompany = company
    this.createKnowledgeArticleFormGroup.patchValue({ companyReferenceId: company.id })
  }

  saveBasicKnowledgeArticle() {
    if (this.createKnowledgeArticleFormGroup.invalid) {
      this.createKnowledgeArticleFormGroup.markAllAsTouched()
      this.showCompanySelectorError = true
      return
    }
    const knowledgeArticle = {
      contents: '',
      heading: this.createKnowledgeArticleFormGroup.get('heading').value,
      referenceVideo: '',
      displayForEmployee: false,
      displayForAdmin: true,
      companyReferenceId: this.selectedCompany.id,
      companyReferenceType: this.selectedCompany.type,
      category: this.createKnowledgeArticleFormGroup.get('category').value,
      subCategory: this.createKnowledgeArticleFormGroup.get('subCategory').value
    } as any
    this.knowledgeService.dispatchSaveKnowledgeAction(knowledgeArticle, null, '')
  }

  onCategorySelected(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection
    if (selectedCategory && selectedSubCategory) {
      this.createKnowledgeArticleFormGroup.patchValue({
        category: selectedCategory,
        subCategory: selectedSubCategory
      })
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }
}
